import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { api } from '../api/sangly';
import { timeAgo } from '../lib/format';
import { cn } from '../lib/utils';
import RunningBoar from './RunningBoar';
import CelebratingBoar, { BOAR_MOODS } from './CelebratingBoar';

/** The sanglier gallops across the top of the screen (a treat after a sync). */
function BoarRun({ onDone }) {
  return (
    <div className="animate-boar-run pointer-events-none fixed left-0 top-1.5 z-[200]" onAnimationEnd={onDone}>
      <RunningBoar size={56} className="drop-shadow-md" />
    </div>
  );
}

/**
 * "Sync now" + a live activity bar. Odoo has no push, so freshness = polling:
 *   • while the app is open we poll status every 15s and auto-trigger ONE sync
 *     when the last one is older than the server's window (backend dedupes);
 *   • while a sync runs we poll every 2s and show the current stage + progress;
 *   • when it finishes we flash a short debrief (added / updated / paid) for a
 *     few seconds, then fall back to "Updated Xm ago".
 */
const STEPS = ['connecting', 'fetching', 'processing', 'finalizing'];
const STAGE_LABEL = {
  connecting: 'Connecting to Odoo',
  fetching: 'Fetching vendor bills',
  processing: 'Syncing invoices',
  finalizing: 'Detecting paid & changes',
  done: 'Done',
  error: 'Error',
};

function debriefText(r) {
  const parts = [];
  if (r.created) parts.push(`${r.created} added`);
  if (r.updated) parts.push(`${r.updated} updated`);
  if (r.newly_paid) parts.push(`${r.newly_paid} marked paid`);
  return parts.length ? parts.join(' · ') : 'No changes';
}

export default function SyncBar({ onSynced }) {
  const [status, setStatus] = useState(null);
  const [debrief, setDebrief] = useState(null);
  const [runBoar, setRunBoar] = useState(false);
  const [mood, setMood] = useState(null);   // celebration variant after a sync
  const wasRunning = useRef(false);
  const triggering = useRef(false);
  const debriefTimer = useRef(null);

  const running = status?.running || null;
  const isRunning = !!running;

  const maybeAutoSync = useCallback((s) => {
    if (!s || s.running || triggering.current) return;
    const win = s.auto_sync_minutes;
    if (!win || win <= 0) return;
    const last = s.last?.finished_at || s.last_success?.finished_at;
    const ageMin = last ? (Date.now() - new Date(last).getTime()) / 60000 : Infinity;
    if (ageMin >= win) {
      triggering.current = true;
      api.sync.run(true).catch(() => {}).finally(() => { triggering.current = false; });
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const s = await api.sync.status();
      setStatus(s);
      if (wasRunning.current && !s.running) {          // a run just finished
        onSynced?.();
        const r = s.last;
        if (r) {
          setDebrief(r.status === 'error'
            ? { ok: false, text: 'Sync failed' }
            : { ok: true, text: debriefText(r) });
          // hard work done - the sanglier celebrates (beer / dance / pétanque)
          if (r.status === 'success') setMood(BOAR_MOODS[Math.floor(Math.random() * BOAR_MOODS.length)]);
          clearTimeout(debriefTimer.current);
          debriefTimer.current = setTimeout(() => { setDebrief(null); setMood(null); }, 7000);
          // and every now and then it gallops right across the top bar (more on changes)
          const changed = r.created || r.updated || r.newly_paid;
          if (r.status === 'success' && Math.random() < (changed ? 0.5 : 0.2)) setRunBoar(true);
        }
      }
      wasRunning.current = !!s.running;
      maybeAutoSync(s);
    } catch { /* transient - ignore */ }
  }, [onSynced, maybeAutoSync]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const t = setInterval(refresh, isRunning ? 2000 : 15000);
    return () => clearInterval(t);
  }, [refresh, isRunning]);
  useEffect(() => () => clearTimeout(debriefTimer.current), []);

  const start = async () => {
    setDebrief(null); setMood(null);
    setStatus((s) => ({ ...(s || {}), running: { stage: 'connecting', processed: 0, total: 0 } }));
    wasRunning.current = true;
    try { await api.sync.run(false); } catch { /* surfaced via status */ }
    refresh();
  };

  const last = status?.last_success;
  const pct = running && running.total > 0
    ? Math.min(100, Math.round((running.processed / running.total) * 100)) : null;
  const stepIdx = running ? STEPS.indexOf(running.stage) : -1;

  return (
    <div className="flex items-center gap-2.5">
      {runBoar && <BoarRun onDone={() => setRunBoar(false)} />}
      {isRunning ? (
        <>
        {/* the boar gallops in place while data is actually being pulled */}
        <RunningBoar size={40} className="-my-1 hidden shrink-0 sm:block" />
        <div className="w-60">
          <div className="mb-1 flex items-center justify-between text-[11px]">
            <span className="flex items-center gap-1.5 font-medium text-foreground">
              <span className="h-2 w-2 rounded-full bg-primary animate-live" />
              {STAGE_LABEL[running.stage] || 'Syncing'}
              {stepIdx >= 0 && <span className="text-muted-foreground">· step {stepIdx + 1}/{STEPS.length}</span>}
            </span>
            <span className="tabular-nums text-muted-foreground">
              {running.total > 0 ? `${running.processed.toLocaleString()}/${running.total.toLocaleString()}` : ''}
              {pct !== null ? ` · ${pct}%` : ''}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            {pct === null ? (
              <div className="h-full w-1/3 rounded-full bg-primary animate-indeterminate" />
            ) : (
              <div className="h-full rounded-full bg-primary transition-[width] duration-500"
                   style={{ width: `${Math.max(6, pct)}%` }} />
            )}
          </div>
        </div>
        </>
      ) : debrief ? (
        <div className={cn('flex items-center gap-2 text-[12px] font-medium',
          debrief.ok ? 'text-emerald-600' : 'text-rose-600')}>
          {debrief.ok && mood
            ? <CelebratingBoar mood={mood} size={46} className="-my-2 shrink-0" />
            : (debrief.ok ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />)}
          <span>{debrief.ok ? 'Synced' : 'Sync'}</span>
          <span className="text-muted-foreground">· {debrief.text}</span>
        </div>
      ) : (
        <div className="hidden items-center gap-1.5 text-[12px] text-muted-foreground sm:flex">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {last ? `Updated ${timeAgo(last.finished_at)}` : 'Not synced yet'}
        </div>
      )}
      <button
        onClick={start}
        disabled={isRunning}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-border bg-card px-3 text-[13px] font-medium shadow-xs transition-colors hover:bg-muted disabled:opacity-60"
        title="Pull the latest vendor bills from Odoo"
      >
        <RefreshCw className={cn('h-4 w-4', isRunning && 'animate-spin')} />
        Sync now
      </button>
    </div>
  );
}
